import React from 'react';

import { Link } from 'react-router-dom';

import {
  CalculatorOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  DownloadOutlined,
  DownOutlined,
  ReconciliationOutlined,
  StopOutlined,
  InsertRowBelowOutlined,
  MergeCellsOutlined,
  ArrowRightOutlined,
} from '@ant-design/icons';
import { TenantUserPermission } from '@recurrency/core-api-schema/dist/common/enums';
import { SearchPlanningDTO } from '@recurrency/core-api-schema/dist/search/getSearchPlanning';
import { Menu } from 'antd';

import { Button } from 'components/Button';
import { Dropdown } from 'components/Dropdown';
import { notifyAndDownload } from 'components/recipes/DownloadButton';
import { SearchFrameContext, ValueFacet } from 'components/recipes/SearchFrame/types';
import { Tooltip } from 'components/Tooltip';

import { useGlobalApp } from 'hooks/useGlobalApp';

import { showAsyncModal } from 'utils/asyncModal';
import { truthy } from 'utils/boolean';
import { hasUserPermission } from 'utils/roleAndTenant';
import { encodeHashState, routes } from 'utils/routes';
import { isTenantErpTypeEclipse } from 'utils/tenants';

import { changeRecordFlagStatus } from './modals/FlagItemStatusUpdater';
import { changeRecordExcludedStatus } from './modals/IncludeExcludeStatusUpdater';
import { MultiLeadTimeModal } from './modals/MultiLeadTimeModal';
import { MultiMinOrderQuantityModal } from './modals/MultiMinOrderQuantityModal';
import { MultiSafetyStockModal } from './modals/MultiSafetyStockModal';
import { MultiStockFloorModal } from './modals/MultiStockFloorModal';
import { MultiUpdateReplenishmentSettingsModalWListSelection } from './modals/MultiUpdateReplenishmentSettingsModal';
import { PendingMinMaxModal } from './modals/PendingMinMaxModal';
import { getMinMaxExportRestrictions, MIN_MAX_EXPORT_LIMIT, PLANNING_WORKFLOW_LIMIT } from './utils';

export const PlanningSearchFrameHeader = ({
  searchFrameContext,
  searchIndexReload,
  clearSelection,
  valueFacets,
  showOpOqVariant = false,
}: {
  searchFrameContext: SearchFrameContext<SearchPlanningDTO>;
  searchIndexReload: () => void;
  clearSelection: () => void;
  valueFacets: (ValueFacet<SearchPlanningDTO> | null)[];
  showOpOqVariant?: boolean;
}) => {
  const { activeTenant, activeUser } = useGlobalApp();
  const { selection, searchIndexResponse, searchRequest } = searchFrameContext;

  const { isTxnApiTenantWithSynchronousExports, isRestrictedToSmallExportsOnly } = getMinMaxExportRestrictions(
    activeTenant,
    activeUser,
  );
  const isOverMinMaxLimit =
    (isTxnApiTenantWithSynchronousExports || isRestrictedToSmallExportsOnly) &&
    selection &&
    selection.count > MIN_MAX_EXPORT_LIMIT;

  const planningWorkflowLimitMaxItems = isTxnApiTenantWithSynchronousExports
    ? MIN_MAX_EXPORT_LIMIT
    : PLANNING_WORKFLOW_LIMIT;

  // Check if the number of items selected or items filtered is over the limit
  const isOverPlanningWorkflowLimit =
    (!selection || (selection.count > 0 && selection.count > planningWorkflowLimitMaxItems)) &&
    (!searchIndexResponse?.nbHits ||
      (!!searchIndexResponse?.nbHits && searchIndexResponse.nbHits > planningWorkflowLimitMaxItems));

  return (
    <>
      <Tooltip
        title={isOverPlanningWorkflowLimit ? `Limit of ${planningWorkflowLimitMaxItems} records exceeded.` : null}
      >
        <Link
          to={{
            pathname: routes.demandPlanning.planningWorkflow(),
            hash: encodeHashState({
              where: searchRequest.filters,
              query: searchRequest.query,
              sortBy: searchRequest.sortBy,
              secondarySortBy: searchRequest.secondarySortBy,
            }),
            state: { selection },
          }}
        >
          <Button type="primary" disabled={isOverPlanningWorkflowLimit}>
            Open in Planning Workflow <ArrowRightOutlined />
          </Button>
        </Link>
      </Tooltip>

      {isTenantErpTypeEclipse(activeTenant.erpType) &&
        (hasUserPermission(activeTenant, TenantUserPermission.ActionExportMinMax) ||
          hasUserPermission(activeTenant, TenantUserPermission.AccessPlanningPlanningDownload)) && (
          <Button icon={<DownloadOutlined />} onClick={() => showAsyncModal(PendingMinMaxModal, {})}>
            Pending Min/Max
          </Button>
        )}

      <Dropdown
        disabled={!selection}
        overlay={
          <Menu>
            <Tooltip
              placement="left"
              title={
                isOverMinMaxLimit
                  ? isRestrictedToSmallExportsOnly
                    ? `Exports of >${MIN_MAX_EXPORT_LIMIT} records are available once onboarding is complete`
                    : `Export is limited to ${MIN_MAX_EXPORT_LIMIT} records at a time`
                  : undefined
              }
            >
              <div>
                <Menu.Item
                  key="minMax"
                  icon={<CalculatorOutlined />}
                  disabled={isOverMinMaxLimit}
                  onClick={() => {
                    // this will never happen, but we appease tsc to ensure it is defined
                    if (!selection) return;
                    if (selection.type === 'filter') {
                      // add filterTitleMapping so we can show Filters used in the modal
                      selection.filterTitleMapping = Object.fromEntries(
                        valueFacets.filter(truthy).map((facet) => [facet.field, facet.title]),
                      );
                    }

                    showAsyncModal(MultiUpdateReplenishmentSettingsModalWListSelection, {
                      selection,
                      searchRequest,
                      isOpOqVariant: showOpOqVariant,
                      searchIndexReload,
                      clearSelection,
                    });
                  }}
                >
                  {showOpOqVariant ? 'OP/OQ' : 'Min/Max'}
                </Menu.Item>
              </div>
            </Tooltip>
            <Menu.Item
              key="safetyStock"
              icon={<ReconciliationOutlined />}
              onClick={() => {
                // this will never happen, but we appease tsc to ensure it is defined
                if (!selection) return;

                // add filterTitleMapping so we can show Filters used in the modal
                if (selection.type === 'filter') {
                  selection.filterTitleMapping = Object.fromEntries(
                    valueFacets.filter(truthy).map((facet) => [facet.field, facet.title]),
                  );
                }
                showAsyncModal(MultiSafetyStockModal, {
                  selection,
                  searchIndexReload,
                  clearSelection,
                });
              }}
            >
              Safety Stock
            </Menu.Item>
            <Menu.Item
              key="leadTime"
              icon={<CalendarOutlined />}
              onClick={() => {
                // this will never happen, but we appease tsc to ensure it is defined
                if (!selection) return;

                // add filterTitleMapping so we can show Filters used in the modal
                if (selection.type === 'filter') {
                  selection.filterTitleMapping = Object.fromEntries(
                    valueFacets.filter(truthy).map((facet) => [facet.field, facet.title]),
                  );
                }
                showAsyncModal(MultiLeadTimeModal, {
                  selection,
                  searchIndexReload,
                  clearSelection,
                });
              }}
            >
              Lead Time
            </Menu.Item>
            <Menu.Item
              key="minStockFloor"
              icon={<InsertRowBelowOutlined />}
              onClick={() => {
                // this will never happen, but we appease tsc to ensure it is defined
                if (!selection) return;

                // add filterTitleMapping so we can show Filters used in the modal
                if (selection.type === 'filter') {
                  selection.filterTitleMapping = Object.fromEntries(
                    valueFacets.filter(truthy).map((facet) => [facet.field, facet.title]),
                  );
                }
                showAsyncModal(MultiStockFloorModal, {
                  selection,
                  searchIndexReload,
                  clearSelection,
                });
              }}
            >
              Add Stock Floor
            </Menu.Item>
            <Menu.Item
              key="minOrderQuantity"
              icon={<MergeCellsOutlined />}
              onClick={() => {
                // this will never happen, but we appease tsc to ensure it is defined
                if (!selection) return;

                // add filterTitleMapping so we can show Filters used in the modal
                if (selection.type === 'filter') {
                  selection.filterTitleMapping = Object.fromEntries(
                    valueFacets.filter(truthy).map((facet) => [facet.field, facet.title]),
                  );
                }
                showAsyncModal(MultiMinOrderQuantityModal, {
                  selection,
                  searchIndexReload,
                  clearSelection,
                });
              }}
            >
              Add Min Order Quantity
            </Menu.Item>
            <Menu.Divider />
            <Menu.Item
              key="exclude"
              icon={<StopOutlined />}
              onClick={async () => {
                // this will never happen, but we appease tsc to ensure it is defined
                if (!selection) return;

                // add filterTitleMapping so we can show Filters used in the modal
                if (selection.type === 'filter') {
                  selection.filterTitleMapping = Object.fromEntries(
                    valueFacets.filter(truthy).map((facet) => [facet.field, facet.title]),
                  );
                }
                const didChange = await changeRecordExcludedStatus(selection, true);
                if (didChange) {
                  clearSelection();
                  searchIndexReload();
                }
              }}
            >
              Exclude
            </Menu.Item>
            <Menu.Item
              key="include"
              icon={<CheckCircleOutlined />}
              onClick={async () => {
                // this will never happen, but we appease tsc to ensure it is defined
                if (!selection) return;

                // add filterTitleMapping so we can show Filters used in the modal
                if (selection.type === 'filter') {
                  selection.filterTitleMapping = Object.fromEntries(
                    valueFacets.filter(truthy).map((facet) => [facet.field, facet.title]),
                  );
                }
                const didChange = await changeRecordExcludedStatus(selection, false);
                if (didChange) {
                  clearSelection();
                  searchIndexReload();
                }
              }}
            >
              Include
            </Menu.Item>
            {/* If it is a single item that is not flagged, we don't want to show the resolve flag option */}
            {selection?.type === 'list' && selection.items.length === 1 && !selection.items[0].flagged ? (
              <></>
            ) : (
              <>
                <Menu.Divider />
                <Menu.Item
                  key="resolve"
                  icon={<CheckCircleOutlined />}
                  onClick={async () => {
                    // this will never happen, but we appease tsc to ensure it is defined
                    if (!selection) return;

                    // add filterTitleMapping so we can show Filters used in the modal
                    if (selection.type === 'filter') {
                      selection.filterTitleMapping = Object.fromEntries(
                        valueFacets.filter(truthy).map((facet) => [facet.field, facet.title]),
                      );
                    }
                    const didChange = await changeRecordFlagStatus(selection, false);
                    if (didChange) {
                      clearSelection();
                      searchIndexReload();
                    }
                  }}
                >
                  Resolve Flag
                </Menu.Item>
              </>
            )}
            {hasUserPermission(activeTenant, TenantUserPermission.AccessPlanningPlanningDownload) && (
              <>
                <Menu.Divider />
                <Menu.Item
                  key="download"
                  icon={<DownloadOutlined />}
                  onClick={() => notifyAndDownload(searchFrameContext.getDownloadData, 'Planning')}
                >
                  Download
                </Menu.Item>
              </>
            )}
          </Menu>
        }
      >
        <Button type="primary" title={!selection ? `Select table records to enable bulk actions` : undefined}>
          Bulk Actions <DownOutlined />
        </Button>
      </Dropdown>
    </>
  );
};
